import React from 'react';

import styled from 'styled-components';

import { Currency, FormattedMessage } from '@techstyle/react-intl';

import { useFreeShipping } from '../../utils/useFreeShipping';
import BorderfreeGuard from '../BorderfreeGuard/BorderfreeGuard';
import { Component as Price } from '../ExtendedPrice';

import { NewCartDrawerProgressBar } from './NewCartDrawerProgressBar';
import { ProgressBarTooltip } from './ProgressBarTooltip';
import { useCartDrawerFreeShippingProgressBar } from './useCartDrawerProgressBar';
import { useHideProgressBar } from './useHideProgressBar';

const LighterPrice = styled(Price)`
  font-weight: 600;
`;

const TransitionContainer = styled.div`
  position: relative;
  min-height: 50px;
  width: 100%;
  height: 100%;
`;

const TransitionWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const TransitionText = styled.div<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  transition: ${({ isVisible }) =>
    isVisible
      ? 'opacity 0.4s cubic-bezier(0.33, 0.01, 0.67, 1), visibility 0s'
      : 'opacity 0s, visibility 0s'};
  width: 100%;
  text-align: center;
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

function NewCartDrawerFreeShippingProgressBarRenderer() {
  const { total, shouldShowProgressBar, threshold } =
    useCartDrawerFreeShippingProgressBar();
  const isFreeShippingFlagEnabled = useFreeShipping(true);

  const tooltip = (
    <ProgressBarTooltip
      placement="auto-end"
      anchorProps={{
        'data-autotag': 'shipping-tooltip',
        'data-qa-automation': 'ShippingTooltip',
      }}
    >
      <FormattedMessage
        id="global_checkout.free_shipping_tooltip"
        defaultMessage="Free shipping on orders over"
      />
      &nbsp;
      <Currency
        amount={threshold}
        currencyDisplay="symbol"
        maximumFractionDigits={2}
        minimumFractionDigits={0}
        trimZeros
        useGrouping
      />
    </ProgressBarTooltip>
  );
  if (isFreeShippingFlagEnabled) {
    return (
      <NewCartDrawerProgressBar
        render={
          <FormattedMessage
            id="global_checkout.free_shipping_congratulation"
            defaultMessage="Congrats! You get Free Shipping."
          />
        }
      />
    );
  } else {
    return (
      <BorderfreeGuard>
        {!shouldShowProgressBar ? null : (
          <NewCartDrawerProgressBar
            progress={{ currentValue: total, goalValue: threshold }}
            render={({ amountToGoal }) => (
              <TransitionContainer>
                <TransitionWrapper>
                  <TransitionText isVisible={amountToGoal === 0}>
                    <FormattedMessage
                      id="global_checkout.free_shipping_congratulation"
                      defaultMessage="Congrats! You get Free Shipping."
                    />
                    {tooltip}
                  </TransitionText>
                </TransitionWrapper>
                <TransitionWrapper>
                  <TransitionText isVisible={amountToGoal !== 0}>
                    <FormattedMessage
                      id="global_checkout.away_from_free_shipping_message"
                      defaultMessage="You are {price} away from Free Shipping!"
                      values={{
                        price: (
                          <strong>
                            <LighterPrice amount={amountToGoal} />
                          </strong>
                        ),
                      }}
                    />
                    {tooltip}
                  </TransitionText>
                </TransitionWrapper>
              </TransitionContainer>
            )}
          />
        )}
      </BorderfreeGuard>
    );
  }
}

export function NewCartDrawerFreeShippingProgressBar() {
  const shouldHideProgressBar = useHideProgressBar();

  if (shouldHideProgressBar) {
    return null;
  }
  return <NewCartDrawerFreeShippingProgressBarRenderer />;
}
